import { MutationTree } from 'vuex';
import {
    AccountInventorySettingState,
    AccountInventorySetting,
    AccountInventorySettingDetail,
    InventoryTargetNodeGroup,
    InventoryTargetNodeDomain,
} from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// エラーをクリアする
const resetErrors = (state: AccountInventorySettingState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorTitle = undefined;
    searchState.errorDetails = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorTitle = undefined;
    showState.errorDetails = undefined;

    const updateState = state.update;
    updateState.error = false;
    updateState.errorTitle = undefined;
    updateState.errorDetails = undefined;

    const inventoryExecuteState = state.executeInventory;
    inventoryExecuteState.error = false;
    inventoryExecuteState.errorTitle = undefined;
    inventoryExecuteState.errorDetails = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;

    const searchUnassignedNodeGroupsState = state.searchUnassignedNodeGroups;
    searchUnassignedNodeGroupsState.error = false;
    searchUnassignedNodeGroupsState.errorTitle = undefined;
    searchUnassignedNodeGroupsState.errorDetails = undefined;

    const addNodeGroupsState = state.addNodeGroups;
    addNodeGroupsState.error = false;
    addNodeGroupsState.errorTitle = undefined;
    addNodeGroupsState.errorDetails = undefined;

    const removeNodeGroupsState = state.removeNodeGroups;
    removeNodeGroupsState.error = false;
    removeNodeGroupsState.errorTitle = undefined;
    removeNodeGroupsState.errorDetails = undefined;

    const searchUnassignedNodeDomainsState = state.searchUnassignedNodeDomains;
    searchUnassignedNodeDomainsState.error = false;
    searchUnassignedNodeDomainsState.errorTitle = undefined;
    searchUnassignedNodeDomainsState.errorDetails = undefined;

    const addNodeDomainsState = state.addNodeDomains;
    addNodeDomainsState.error = false;
    addNodeDomainsState.errorTitle = undefined;
    addNodeDomainsState.errorDetails = undefined;

    const removeNodeDomainsState = state.removeNodeDomains;
    removeNodeDomainsState.error = false;
    removeNodeDomainsState.errorTitle = undefined;
    removeNodeDomainsState.errorDetails = undefined;

    const updateSecurityGroupsState = state.updateSecurityGroups;
    updateSecurityGroupsState.error = false;
    updateSecurityGroupsState.errorTitle = undefined;
    updateSecurityGroupsState.errorDetails = undefined;
};

export const mutations: MutationTree<AccountInventorySettingState> = {
    // アカウント棚卸設定検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](
        state,
        payload: { count: number; account_inventory_settings: Array<AccountInventorySetting> }
    ) {
        state.accountInventorySettingCount = payload.count;
        state.accountInventorySettings = payload.account_inventory_settings;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const searchState = state.search;
        searchState.errorTitle = payload.errorTitle;
        searchState.errorDetails = payload.errorDetails;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // アカウント棚卸設定作成系
    [types.CLEAR_ERROR_ACCOUNT_INVENTORY_SETTING](state) {
        resetErrors(state);
        const createState = state.create;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        resetErrors(state);
        const createState = state.create;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state) {
        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // アカウント棚卸設定詳細情報参照系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: AccountInventorySettingDetail) {
        const showState = state.show;
        state.accountInventorySettingDetails = payload;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const showState = state.show;
        showState.errorTitle = payload.errorTitle;
        showState.errorDetails = payload.errorDetails;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // アカウント棚卸設定更新系
    [types.UPDATE_START](state) {
        resetErrors(state);
        const updateState = state.update;
        updateState.isProcessing = true;
    },

    [types.UPDATE_SUCCESS](state) {
        const updateState = state.update;
        updateState.isProcessing = false;
    },

    [types.UPDATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateState = state.update;
        updateState.errorTitle = payload.errorTitle;
        updateState.errorDetails = payload.errorDetails;
        updateState.error = true;
        updateState.isProcessing = false;
    },

    // アカウント棚卸即時実行系
    [types.EXECUTE_INVENTORY_START](state) {
        resetErrors(state);
        const inventoryExecuteState = state.executeInventory;
        inventoryExecuteState.isProcessing = true;
    },

    [types.EXECUTE_INVENTORY_SUCCESS](state) {
        const inventoryExecuteState = state.executeInventory;
        inventoryExecuteState.isProcessing = false;
    },

    [types.EXECUTE_INVENTORY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const inventoryExecuteState = state.executeInventory;
        inventoryExecuteState.errorTitle = payload.errorTitle;
        inventoryExecuteState.errorDetails = payload.errorDetails;
        inventoryExecuteState.error = true;
        inventoryExecuteState.isProcessing = false;
    },

    // アカウント棚卸設定削除系
    [types.DESTROY_START](state) {
        resetErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // 棚卸設定の未追加ノードグループ検索系
    [types.SEARCH_UNASSIGNED_NODE_GROUPS_START](state) {
        resetErrors(state);
        const searchUnassignedNodeGroupsState = state.searchUnassignedNodeGroups;
        searchUnassignedNodeGroupsState.isProcessing = true;
        searchUnassignedNodeGroupsState.isFetchSuccess = false;
    },

    [types.SEARCH_UNASSIGNED_NODE_GROUPS_SUCCESS](
        state,
        payload: { count: number; unassigned_node_group: Array<InventoryTargetNodeGroup> }
    ) {
        state.unassignedNodeGroups = payload;
        const searchUnassignedNodeGroupsState = state.searchUnassignedNodeGroups;
        searchUnassignedNodeGroupsState.isProcessing = false;
        searchUnassignedNodeGroupsState.isFetchSuccess = true;
    },

    [types.SEARCH_UNASSIGNED_NODE_GROUPS_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const searchUnassignedNodeGroupsState = state.searchUnassignedNodeGroups;
        searchUnassignedNodeGroupsState.errorTitle = payload.errorTitle;
        searchUnassignedNodeGroupsState.errorDetails = payload.errorDetails;
        searchUnassignedNodeGroupsState.error = true;
        searchUnassignedNodeGroupsState.isProcessing = false;
        searchUnassignedNodeGroupsState.isFetchSuccess = false;
    },

    // 棚卸対象ノードグループ追加系
    [types.ADD_NODE_GROUPS_START](state) {
        resetErrors(state);
        const addNodeGroupsState = state.addNodeGroups;
        addNodeGroupsState.isProcessing = true;
    },

    [types.ADD_NODE_GROUPS_SUCCESS](state) {
        const addNodeGroupsState = state.addNodeGroups;
        addNodeGroupsState.isProcessing = false;
    },

    [types.ADD_NODE_GROUPS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addNodeGroupsState = state.addNodeGroups;
        addNodeGroupsState.errorTitle = payload.errorTitle;
        addNodeGroupsState.errorDetails = payload.errorDetails;
        addNodeGroupsState.error = true;
        addNodeGroupsState.isProcessing = false;
    },

    // 棚卸対象ノードグループ除外系
    [types.REMOVE_NODE_GROUPS_START](state) {
        resetErrors(state);
        const removeNodeGroupsState = state.removeNodeGroups;
        removeNodeGroupsState.isProcessing = true;
    },

    [types.REMOVE_NODE_GROUPS_SUCCESS](state) {
        const removeNodeGroupsState = state.removeNodeGroups;
        removeNodeGroupsState.isProcessing = false;
    },

    [types.REMOVE_NODE_GROUPS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeNodeGroupsState = state.removeNodeGroups;
        removeNodeGroupsState.errorTitle = payload.errorTitle;
        removeNodeGroupsState.errorDetails = payload.errorDetails;
        removeNodeGroupsState.error = true;
        removeNodeGroupsState.isProcessing = false;
    },

    // 棚卸設定の未追加ノードドメイン検索系
    [types.SEARCH_UNASSIGNED_NODE_DOMAINS_START](state) {
        resetErrors(state);
        const searchUnassignedNodeDomainsState = state.searchUnassignedNodeDomains;
        searchUnassignedNodeDomainsState.isProcessing = true;
        searchUnassignedNodeDomainsState.isFetchSuccess = false;
    },

    [types.SEARCH_UNASSIGNED_NODE_DOMAINS_SUCCESS](
        state,
        payload: { count: number; unassigned_node_domain: Array<InventoryTargetNodeDomain> }
    ) {
        state.unassignedNodeDomains = payload;
        const searchUnassignedNodeDomainsState = state.searchUnassignedNodeDomains;
        searchUnassignedNodeDomainsState.isProcessing = false;
        searchUnassignedNodeDomainsState.isFetchSuccess = true;
    },

    [types.SEARCH_UNASSIGNED_NODE_DOMAINS_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const searchUnassignedNodeDomainsState = state.searchUnassignedNodeDomains;
        searchUnassignedNodeDomainsState.errorTitle = payload.errorTitle;
        searchUnassignedNodeDomainsState.errorDetails = payload.errorDetails;
        searchUnassignedNodeDomainsState.error = true;
        searchUnassignedNodeDomainsState.isProcessing = false;
        searchUnassignedNodeDomainsState.isFetchSuccess = false;
    },

    // 棚卸対象ノードドメイン追加系
    [types.ADD_NODE_DOMAINS_START](state) {
        resetErrors(state);
        const addNodeDomainsState = state.addNodeDomains;
        addNodeDomainsState.isProcessing = true;
    },

    [types.ADD_NODE_DOMAINS_SUCCESS](state) {
        const addNodeDomainsState = state.addNodeDomains;
        addNodeDomainsState.isProcessing = false;
    },

    [types.ADD_NODE_DOMAINS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const addNodeDomainsState = state.addNodeDomains;
        addNodeDomainsState.errorTitle = payload.errorTitle;
        addNodeDomainsState.errorDetails = payload.errorDetails;
        addNodeDomainsState.error = true;
        addNodeDomainsState.isProcessing = false;
    },

    // 棚卸対象ノードドメイン除外系
    [types.REMOVE_NODE_DOMAINS_START](state) {
        resetErrors(state);
        const removeNodeDomainsState = state.removeNodeDomains;
        removeNodeDomainsState.isProcessing = true;
    },

    [types.REMOVE_NODE_DOMAINS_SUCCESS](state) {
        const removeNodeDomainsState = state.removeNodeDomains;
        removeNodeDomainsState.isProcessing = false;
    },

    [types.REMOVE_NODE_DOMAINS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const removeNodeDomainsState = state.removeNodeDomains;
        removeNodeDomainsState.errorTitle = payload.errorTitle;
        removeNodeDomainsState.errorDetails = payload.errorDetails;
        removeNodeDomainsState.error = true;
        removeNodeDomainsState.isProcessing = false;
    },

    // 棚卸対象ノードドメインのセキュリティグループ更新系
    [types.UPDATE_SECURITY_GROUPS_START](state) {
        resetErrors(state);
        const updateSecurityGroupsState = state.updateSecurityGroups;
        updateSecurityGroupsState.isProcessing = true;
    },

    [types.UPDATE_SECURITY_GROUPS_SUCCESS](state) {
        const updateSecurityGroupsState = state.updateSecurityGroups;
        updateSecurityGroupsState.isProcessing = false;
    },

    [types.UPDATE_SECURITY_GROUPS_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const updateSecurityGroupsState = state.updateSecurityGroups;
        updateSecurityGroupsState.errorTitle = payload.errorTitle;
        updateSecurityGroupsState.errorDetails = payload.errorDetails;
        updateSecurityGroupsState.error = true;
        updateSecurityGroupsState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.accountInventorySettings = undefined;
        state.accountInventorySettingCount = 0;
    },
};
